<script lang="ts">
  import { onMount } from 'svelte';
  import type { Commentable, UserType, CommentType } from './comments/types';
  import CommentForm from './comments/CommentForm.svelte';
  import CommentList from './comments/CommentList.svelte';
  import { commentService } from '../services/commentService';
  import { ApiError } from '@services/api';

  let { commentable = { type: '', id: '' } }: { commentable?: Commentable } =
    $props();

  let currentUser = $state<UserType | null>(null);
  let comments = $state<CommentType[]>([]);
  let sortOption = $state('best');
  let loading = $state(false);
  let error = $state<Error | null>(null);
  let page = $state(1);
  let hasMore = $state(false);
  let replyingTo = $state<number | null>(null);

  const sortOptions = [
    { value: 'best', label: 'Melhores', iconClass: 'far fa-star' },
    { value: 'newest', label: 'Mais recentes', iconClass: 'far fa-clock' },
    { value: 'oldest', label: 'Mais antigos', iconClass: 'fas fa-history' },
  ];

  // Define the types needed for processing comments
  type DisplayCommentType = CommentType & { parent_username?: string };
  type ProcessedCommentType = CommentType & {
    displayReplies?: DisplayCommentType[];
  };

  onMount(() => {
    loadUser();
    loadComments();
  });

  async function loadUser() {
    try {
      currentUser = await commentService.getCurrentUser();
    } catch (e: unknown) {}
  }

  async function loadComments() {
    loading = true;
    try {
      const currentSort = sortOption;
      const currentPage = page;
      const currentComments = comments;

      const { comments: newComments, has_more } =
        await commentService.loadComments(
          commentable,
          currentSort,
          currentPage,
        );

      comments =
        currentPage === 1 ? newComments : [...currentComments, ...newComments];
      hasMore = has_more;
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        error = e;
      } else {
        error = e instanceof Error ? e : new Error(String(e));
      }
    } finally {
      loading = false;
    }
  }

  function loadMore() {
    page++;
    loadComments();
  }

  function handleSortChange(newSortOption: string) {
    if (newSortOption !== sortOption) {
      sortOption = newSortOption;
      page = 1;
      loadComments();
    }
  }

  function handleCommentSubmitted(comment: CommentType) {
    comments = [comment, ...comments];
  }

  function handleReplySubmitted(reply: CommentType) {
    const parent = comments.find((c) => c.id === reply.parent_id);
    if (parent) {
      parent.replies = [...parent.replies, reply];
    } else {
      comments = [reply, ...comments];
    }

    replyingTo = null;
  }

  function handleVote(comment: CommentType) {
    const parent = comments.find((c) => c.id === comment.parent_id);
    if (parent) {
      parent.replies = parent.replies?.map((c) =>
        c.id === comment.id ? comment : c,
      );
    } else {
      comments = comments.map((c) => {
        if (c.id === comment.id) {
          // Make sure we preserve the replies array when updating the comment
          return { ...comment, replies: c.replies || [] };
        }
        return c;
      });
    }
  }

  function removeCommentFromTree(
    list: CommentType[],
    commentId: number,
  ): CommentType[] {
    const filteredList = list.filter((comment) => comment.id !== commentId);

    return filteredList.map((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        const updatedReplies = removeCommentFromTree(
          comment.replies,
          commentId,
        );
        if (updatedReplies !== comment.replies) {
          return { ...comment, replies: updatedReplies };
        }
      }
      return comment;
    });
  }

  function handleDelete(commentId: number) {
    comments = removeCommentFromTree(comments, commentId);
  }
</script>

<div class="comments-container">
  <h3>Comentários</h3>

  {#if error}
    <div class="error">{error.message}</div>
  {/if}

  <div class="sort-controls">
    {#each sortOptions as option (option.value)}
      <button
        class="sort-tab"
        class:active={sortOption === option.value}
        onclick={() => handleSortChange(option.value)}
        aria-pressed={sortOption === option.value}
      >
        <i class={`${option.iconClass} sort-icon`}></i>
        {option.label}
      </button>
    {/each}
  </div>

  {#if currentUser}
    <CommentForm
      {commentable}
      {loading}
      onSubmit={(comment) => handleCommentSubmitted(comment)}
      onError={(e) => (error = e)}
    />
  {:else}
    <div class="login-prompt">
      <a href="/u/entrar">Acesse sua conta</a> para deixar um comentário
    </div>
  {/if}

  <CommentList
    {comments}
    {currentUser}
    {commentable}
    onVote={handleVote}
    {replyingTo}
    onReplyClick={(id) => (replyingTo = id)}
    onReplyCancel={() => (replyingTo = null)}
    onReplySubmitted={(comment) => handleReplySubmitted(comment)}
    onDelete={handleDelete}
    onError={(e) => (error = e)}
    {loading}
    {hasMore}
    onLoadMore={loadMore}
  />
</div>

<style lang="scss">
  .comments-container {
    padding: 15px;
    border: 1px solid #eee;
    border-radius: 8px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
      Ubuntu, Cantarell, sans-serif;
    overflow-x: hidden;

    :global(.dark) :global(#chapter-section) & {
      border-color: #5e5e5e;
    }
  }

  h3 {
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 18px;
    font-weight: 500;
  }

  .sort-controls {
    margin-bottom: 20px;
    display: flex;
    gap: 8px;
    border-bottom: 1px solid #eee;
    overflow-x: auto;
    white-space: nowrap;
    -ms-overflow-style: none;
    scrollbar-width: none;

    .sort-icon {
      margin-right: 4px;
    }
  }

  .sort-controls::-webkit-scrollbar {
    display: none;
  }

  .sort-tab {
    background: none;
    border: none;
    padding: 8px 12px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    color: #878a8c;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition:
      color 0.2s ease,
      border-color 0.2s ease;
    flex-shrink: 0;

    &:hover {
      color: #1a1a1b;
    }

    &.active {
      color: #0079d3;
      border-bottom-color: #0079d3;
    }
    :global(.dark) :global(#chapter-section) & {
      color: #cccccc; /* Adjusted color for dark mode */
    }

    :global(.dark) :global(#chapter-section) &:hover {
      color: #b3931d; /* Adjusted hover color for dark mode */
    }

    :global(.dark) :global(#chapter-section) &.active {
      color: #f5c109; /* Adjusted active color for dark mode */
      border-bottom-color: #f5c109;
    }
  }

  .login-prompt {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 24px;
    color: #777;
    margin: 0 15px;
    text-align: center;

    :global(.dark) :global(#chapter-section) & {
      border-color: #4e4c4c;
      color: #ccc;
    }
  }

  .login-prompt a {
    color: #0079d3;
    text-decoration: none;
  }

  .error {
    color: #ff4500;
    padding: 12px;
    background-color: #fff3f0;
    border-radius: 4px;
    margin-bottom: 16px;
  }

  @media (max-width: 600px) {
    .comments-container {
      padding: 10px 0;
      margin: 10px 0 0 0;
      border-radius: 0;
      border: none;
      border-top: 1px solid #eee;
      border-bottom: 1px solid #eee;
      max-width: 100%;
    }

    h3 {
      font-size: 16px;
      margin-bottom: 12px;
      padding: 0 10px;
    }

    .sort-controls {
      margin-bottom: 16px;
      padding: 0 10px;
      border-bottom: 1px solid #eee;
    }

    .sort-tab {
      padding: 6px 10px;
      font-size: 13px;
    }

    .login-prompt {
      padding: 0 10px;
      margin-bottom: 16px;
    }

    .error {
      margin: 0 10px 16px 10px;
    }
  }
</style>
